import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { Picture } from 'formlabs-web-components/Media';
import { ColorOverlay } from 'formlabs-web-components/Media';
import { Eyebrow } from 'formlabs-web-components/Type';
import { Link, Button } from 'formlabs-web-components/Link';
import { DisplayComponent } from 'formlabs-web-components/BaseComponents';

import useIsMobile from '../../hooks/useIsMobile';
import './Card.module.css';

const Card = ({
  bgColor,
  cardStyle = 'custom',
  hover,
  hasArrow,
  videoOverlay,
  border,
  borderTopBottom,
  collapseBorder,
  rounded,

  padding,
  internalPadding,
  url,

  image,
  imagePosition,
  imageConstraint,
  pinImage,
  imageAlt,
  imageOverlay,
  unresponsive,

  topLeftLabelText,
  eyebrowText,
  eyebrowColor = 'blue',
  bottomRightLabelText,
  titleStyle,
  contentAlignment,
  title,
  caption,
  subtitle,
  body,
  children,
  ctaText
}) => {
  const isMobile = useIsMobile(640);
  const position = !unresponsive && isMobile ? 'top' : imagePosition;

  // Define what content to show in each card layout style
  const display = {
    detailed: {
      image: true,
      eyebrow: true,
      title: true,
      subtitle: true,
      caption: true,
      cta: true
    },
    video: {
      image: true,
      eyebrow: true,
      title: true,
      subtitle: true,
      caption: true,
      timecode: true,
      cta: true
    },
    horizontal_large: {
      image: true,
      eyebrow: true,
      title: true,
      subtitle: true,
      caption: true
    },
    horizontal_small: {
      image: true,
      eyebrow: true,
      title: true,
      subtitle: true
    },
    simple_vertical: {
      image: true,
      title: true,
      caption: true
    },
    simple_horizontal: {
      image: true,
      title: true,
      caption: true
    },
    tile: {
      image: true,
      title: true
    }
  };

  const eyebrowColorCheck =
    (!bgColor || bgColor === 'white' || bgColor === 'transparent-black') &&
    eyebrowColor;

  // leaving the legacy layout intact under the "custom" card style to make sure existing cards are unchanged
  const cardContent = (
    <>
      {cardStyle === 'custom' ? (
        <div
          styleName={classNames(
            'card',
            position,
            'custom',
            `${contentAlignment || 'left'}-align`,
            {
              'video-overlay': videoOverlay,
              'internal-none': internalPadding === 'none',
              'internal-big': internalPadding === 'big',
              'internal-medium':
                !internalPadding || internalPadding === 'medium',
              'pin-image': pinImage,
              reverse: imagePosition === 'bottom'
            }
          )}
          style={{
            padding: isMobile ? '2rem 1rem' : padding
          }}
        >
          {image && (
            <div
              styleName={classNames('card-image', imageOverlay)}
              style={{
                width: imageConstraint || null
              }}
            >
              {imageOverlay && <ColorOverlay imageOverlay={imageOverlay} />}
              {topLeftLabelText && (
                <div styleName="label-topleft">{topLeftLabelText}</div>
              )}
              <Picture largeImage={image} width="100%" alt={imageAlt} />
              {bottomRightLabelText && (
                <div styleName="label-bottomright">{bottomRightLabelText}</div>
              )}
            </div>
          )}

          <div
            styleName="card-body"
            style={{ flexDirection: isMobile && ctaText ? 'column' : 'row' }}
          >
            <div styleName="card-content">
              {eyebrowText && (
                <div styleName="eyebrow">
                  <Eyebrow color={eyebrowColorCheck}>{eyebrowText}</Eyebrow>
                </div>
              )}
              {title && (
                <h4 styleName={classNames('title', titleStyle)}>{title}</h4>
              )}
              {caption && <p styleName="caption">{caption}</p>}
              {children}
            </div>
            {hasArrow && <i styleName="arrow" className="fl-arrow-right" />}
            {ctaText && (
              <div styleName="cta">
                <Button
                  url={url}
                  color="light"
                  styles={{ height: 'fit-content', width: '100%' }}
                >
                  {ctaText}
                </Button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div styleName={classNames('card', { linkedCard: url })}>
          {display[`${cardStyle}`].image && image && (
            <div styleName="card-image">
              <Picture
                largeImage={image}
                fullBleed={
                  cardStyle === 'detailed' ||
                  cardStyle === 'video' ||
                  cardStyle === 'simple_vertical' ||
                  cardStyle === 'simple_horizontal' ||
                  cardStyle === 'tile'
                }
                width="100%"
                alt={imageAlt}
              />
              {display[`${cardStyle}`].timecode && bottomRightLabelText && (
                <div styleName="label-bottomright">{bottomRightLabelText}</div>
              )}
            </div>
          )}
          <div styleName="card-body">
            <div styleName="card-content">
              {display[`${cardStyle}`].eyebrow && eyebrowText && (
                <div styleName="eyebrow">
                  <Eyebrow color={eyebrowColorCheck}>{eyebrowText}</Eyebrow>
                </div>
              )}
              {title && <h4 styleName="title">{title}</h4>}
              {display[`${cardStyle}`].subtitle && subtitle && (
                <p styleName="subtitle">{subtitle}</p>
              )}
              {display[`${cardStyle}`].caption && caption && (
                <p styleName="caption">{caption}</p>
              )}
              {display[`${cardStyle}`].cta && ctaText && (
                <div styleName="cta">
                  <Button
                    url={url}
                    color={
                      (bgColor === 'black') | (bgColor === 'primary')
                        ? 'white'
                        : 'dark'
                    }
                    styles={{ height: 'fit-content' }}
                  >
                    {ctaText}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );

  return (
    <>
      {cardStyle === 'custom' ? (
        <div
          styleName={classNames(
            'card-wrapper',
            imagePosition,
            bgColor || 'white',
            {
              border,
              'border-topbottom': borderTopBottom,
              collapse: collapseBorder,
              rounded,
              link: url,
              'hover-light-grey': !hover || hover === 'light-grey',
              'hover-blue-outline': hover === 'blue-outline',
              'hover-box-shadow': hover === 'box-shadow',
              'hover-none': hover === 'none'
            }
          )}
        >
          {url && !ctaText ? (
            <Link to={url} openExternalInNewWindow>
              {cardContent}
            </Link>
          ) : (
            cardContent
          )}
        </div>
      ) : (
        <div
          styleName={classNames(
            'card-wrapper',
            cardStyle,
            { detailed: cardStyle === 'video' },
            bgColor || 'white',
            {
              link: url
            },
            { hoverColor: url && !ctaText },
            { pinnedCta: url && ctaText },
            { noImage: !image }
          )}
        >
          {(url && !ctaText) | (url && !cardStyle.cta) ? (
            <Link to={url} openExternalInNewWindow>
              {cardContent}
            </Link>
          ) : (
            cardContent
          )}
        </div>
      )}
    </>
  );
};

Card.propTypes = {
  /** Changes the background color of the card and text to match.
   *  Also a CMS prop. */
  bgColor: PropTypes.string,
  /** What the hover state looks like if the card is a link.
   *  Also a CMS prop. */
  hover: PropTypes.oneOf(['light-grey', 'box-shadow', 'none']),
  /** Add blue right-arrow to the body of the card.
   *  Also a CMS prop. */
  hasArrow: PropTypes.bool,
  /** Add fake play button over image of the card.
   *  Also a CMS prop. */
  videoOverlay: PropTypes.bool,
  /** Display grey border around the whole card.
   *  Also a CMS prop. */
  border: PropTypes.bool,
  /** Display grey border on top and bottom of card only.
   *  Also a CMS prop. */
  borderTopBottom: PropTypes.bool,
  /** Remove top border on sequential cards (for a nice grid/table effect).
   *  Also a CMS prop. */
  collapseBorder: PropTypes.bool,
  /** Give the card rounded corners.
   *  Also a CMS prop. */
  rounded: PropTypes.bool,

  /** Padding around the entire contents of the card (including image), written in CSS.
   *  Also a CMS prop. */
  padding: PropTypes.string,
  /** Padding around just the body of the card.
   *  Also a CMS prop, but "big" is not selectable. */
  internalPadding: PropTypes.oneOf(['none', 'medium', 'big']),

  /** Links the entire card to the designated URL.
   *  Also a CMS prop. */
  url: PropTypes.string.isRequired,

  /** URL for the large image in the card.
   *  Also a CMS prop. */
  image: PropTypes.string,
  /** Alt text for the image.
   *  Also a CMS prop. */
  imageAlt: PropTypes.string,
  /** Determines if image will be above or to the side of the body area.
   *  Also a CMS prop. */
  imagePosition: PropTypes.string,
  /** Restricts width of image if image is in left position.
   *  Also a CMS prop. */
  imageConstraint: PropTypes.string,
  /** Stops left-image cards from turning into top-image cards on mobile.
   *  Not a CMS prop. */
  unresponsive: PropTypes.bool,

  /** Eyebrow text that goes above body area.
   *  Also a CMS prop. */
  eyebrowText: PropTypes.string,
  /** Label text that appears in upper left corner of the card image.
   *  Also a CMS prop. */
  topLeftLabelText: PropTypes.string,
  /** Label text that appears in bottom right corner of the card image.
   *  Also a CMS prop. */
  bottomRightLabelText: PropTypes.string,
  /** Style for title text.
   *  Also a CMS prop. */
  titleStyle: PropTypes.oneOf(['normal', 'micro', 'thin']),
  /** The below are CMS props. */
  title: PropTypes.string,
  caption: PropTypes.string || PropTypes.node,
  body: PropTypes.node,
  children: PropTypes.any
};

export default DisplayComponent(Card);
